import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-toastify';

// Async thunk to save total budget to database
export const saveTotalBudget = createAsyncThunk(
  'totalBudget/saveTotalBudget',
  async ({ yearId, companyId, totalBudget }, { rejectWithValue }) => {
    try {
      // First check if a record already exists
      const { data: existingRecord, error: checkError } = await supabase
        .from('configurations')
        .select('id')
        .eq('year_id', yearId)
        .eq('company_id', companyId)
        .maybeSingle();

      let result;

      if (checkError) {
        throw checkError;
      }

      if (!existingRecord) {
        // Record doesn't exist, insert new one
        const { data, error } = await supabase
          .from('configurations')
          .insert({
            year_id: yearId,
            company_id: companyId,
            total_budget: totalBudget,
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;
        result = data;
        toast.success('Total budget saved successfully!');
      } else {
        // Record exists, update it
        const { data, error } = await supabase
          .from('configurations')
          .update({ 
            total_budget: totalBudget,
            updated_at: new Date().toISOString()
          })
          .eq('year_id', yearId)
          .eq('company_id', companyId)
          .select()
          .single();

        if (error) throw error;
        result = data;
        toast.success('Total budget updated successfully!');
      }

      return result;
    } catch (error) {
      console.error('Error saving total budget:', error);
      toast.error('Failed to save total budget');
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update total budget (for explicit updates)
export const updateTotalBudget = createAsyncThunk(
  'totalBudget/updateTotalBudget',
  async ({ yearId, companyId, totalBudget }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('configurations')
        .update({ 
          total_budget: totalBudget,
          updated_at: new Date().toISOString()
        })
        .eq('year_id', yearId)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;

      toast.success('Total budget updated successfully!');
      return data;
    } catch (error) {
      console.error('Error updating total budget:', error);
      toast.error('Failed to update total budget');
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch total budget from database
export const fetchTotalBudget = createAsyncThunk(
  'totalBudget/fetchTotalBudget',
  async ({ yearId, companyId }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('configurations')
        .select('total_budget')
        .eq('year_id', yearId)
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) {
        throw error;
      }
      
      // Always return a number so MUI Slider/Progress consumers stay type-safe
      const nextBudget = Number(data?.total_budget);
      return Number.isFinite(nextBudget) ? nextBudget : 0;
    } catch (error) {
      console.error('Error fetching total budget:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  totalBudget: 0,
  loading: false,
  error: null
};

const totalBudgetSlice = createSlice({
  name: 'totalBudget',
  initialState,
  reducers: {
    setTotalBudget: (state, action) => {
      const nextBudget = Number(action.payload);
      state.totalBudget = Number.isFinite(nextBudget) ? nextBudget : 0;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Save Total Budget
      .addCase(saveTotalBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTotalBudget.fulfilled, (state, action) => {
        state.loading = false;
        const nextBudget = Number(action.payload?.total_budget);
        if (Number.isFinite(nextBudget)) {
          state.totalBudget = nextBudget;
        }
      })
      .addCase(saveTotalBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Total Budget
      .addCase(updateTotalBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTotalBudget.fulfilled, (state, action) => {
        state.loading = false;
        const nextBudget = Number(action.payload?.total_budget);
        if (Number.isFinite(nextBudget)) {
          state.totalBudget = nextBudget;
        }
      })
      .addCase(updateTotalBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Total Budget
      .addCase(fetchTotalBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTotalBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.totalBudget = action.payload;
      })
      .addCase(fetchTotalBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setTotalBudget, setLoading, setError } = totalBudgetSlice.actions;
export default totalBudgetSlice.reducer; 